import { html, ref, createRef, nothing, OmniElement, OmniStyle } from 'omni-ui';

OmniStyle.register();

export class Om2ClampText extends OmniElement {
  constructor() {
    super();
    this.textRef = createRef();
    this.lessRef = createRef();
    this.maxLines = 2;
    this.readMoreText = 'read more';
  }

  static properties = {
    text: { type: String },
    maxLines: { type: Number },
    readMoreText: { type: String },
    readLessText: { type: String },
    background: { type: String },

    hasOverflow: { state: true },
    expanded: { state: true },
  };

  // No shadow root (and therefore no component css styles)
  // This allows inheriting all text styling from the parent
  createRenderRoot() {
    return this;
  }

  async firstUpdated() {
    // When our text changes size re-compute whether it is overflowing
    const observer = new ResizeObserver(entries => {
      for (const { target } of entries) {
        this.hasOverflow = target.clientHeight < target.scrollHeight;
      }
    });
    observer.observe(this.textRef.value);

    // Figure out background color if not set
    if (!this.background) {
      const isTransparent = color => {
        switch ((color || '').replace(/\s+/g, '').toLowerCase()) {
          case 'transparent':
          case '':
          case 'rgba(0,0,0,0)':
            return true;
          default:
            return false;
        }
      };
      const searchForBGColor = node => {
        if (!node) return 'white';
        const color = node
          .computedStyleMap()
          .get('background-color')
          .toString();
        if (isTransparent(color)) {
          return searchForBGColor(
            node.parentElement ?? node.getRootNode().host
          );
        }
        return color;
      };
      this.background = searchForBGColor(this);
    }
  }

  #textButtonTemplate(label, refObj) {
    const style = [
      `background: linear-gradient(
        90deg,
        rgba(255,255,255,0),
        var(--om2-background-color, ${this.background}) 3.5em
        )`,
      'padding-left: 4em',
      'padding-right: 0.5em',
      'position: absolute',
      'right: 0',
      'bottom: 0',
    ];

    return html`
      <div
        style="${style.join(';')}"
        class="has-text-primary"
        ${refObj ? ref(refObj) : nothing}
      >
        <span
          style="font-size:85%; cursor:pointer"
          @click=${() => {
            this.expanded = !this.expanded;
          }}
          @keyup=${e => e.key === 'Enter' && e.target.click()}
          >${label}</span
        >
      </div>
    `;
  }

  render() {
    return html`
      <div
        class="is-relative"
        @mouseover=${() => {
          if (this.expanded && this.lessRef.value)
            // eslint-disable-next-line no-param-reassign
            this.lessRef.value.style.display = 'block';
        }}
        @mouseout=${() => {
          if (this.expanded && this.lessRef.value)
            // eslint-disable-next-line no-param-reassign
            this.lessRef.value.style.display = 'none';
        }}
        @focus=${e => e.target.mouseover(e)}
        @blur=${e => e.target.mouseout(e)}
      >
        <div
          ${ref(this.textRef)}
          style="display: ${this.expanded
            ? 'block'
            : '-webkit-box'}; -webkit-line-clamp: ${this
            .maxLines}; -webkit-box-orient: vertical; overflow: hidden;"
        >
          ${this.text}
        </div>
        ${this.hasOverflow && !this.expanded
          ? this.#textButtonTemplate(this.readMoreText)
          : nothing}
        ${this.expanded && this.readLessText
          ? this.#textButtonTemplate(this.readLessText, this.lessRef)
          : nothing}
      </div>
    `;
  }
}

OmniElement.register('om2-clamp-text', Om2ClampText);
